import { useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { environment } from '@/_shared/configuration/env';
import { FeatureFlag } from '@/_shared/constants';
import { useFeatureFlags } from '@/_shared/hooks';
import { getUserProfileCall } from '@/core/state';
import { useLogin } from '@/login/state';

export const Chatbot = () => {
  const { isFeatureEnabled } = useFeatureFlags();
  const chatbot = isFeatureEnabled(FeatureFlag.CHATBOT);
  const { isUserLogged } = useLogin();
  const { activeContext } = getUserProfileCall.hook();

  // effect to add inline CSS for z-index
  useEffect(() => {
    if (!chatbot || !isUserLogged || typeof document === 'undefined') {
      return undefined;
    }

    // also apply inline as soon as the container exists
    const applyCssInline = () => {
      const node = document.getElementById('aurora-widget-container') as HTMLElement | null;
      if (node) {
        node.style.setProperty('z-index', '9999999', 'important');
        return true;
      }
      return false;
    };

    // if it's already present now, apply and exit
    if (applyCssInline()) {
      return undefined;
    }

    // otherwise, observe the DOM until the container appears, then apply and disconnect
    const observer = new MutationObserver((_mutationRecord, obs) => {
      if (applyCssInline()) {
        obs.disconnect();
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    // safety: stop observing after 30s to avoid leaks
    const stopTimer = window.setTimeout(() => observer.disconnect(), 30000);

    return () => {
      window.clearTimeout(stopTimer);
      observer.disconnect();
    };
    // same dependencies of the main effect
  }, [chatbot, isUserLogged, activeContext]);

  return (
    <HelmetProvider>
      <Helmet>
        {isUserLogged && chatbot && activeContext === 'ADMIN' ? (
          <script
            id='my-chatbot'
            src={environment.VITE_APP_SRC_WIDGET}
            data-widget-key={environment.VITE_APP_ADMIN_DASHBOARD_WIDGET_KEY}
            data-key-id={environment.VITE_APP_ADMIN_DASHBOARD_KEY_ID}
            data-widget-id={environment.VITE_APP_ADMIN_DASHBOARD_WIDGET_ID}
          />
        ) : null}
        {isUserLogged && chatbot && activeContext === 'LEARNING' ? (
          <script
            id='my-chatbot'
            src={environment.VITE_APP_SRC_WIDGET}
            data-widget-key={environment.VITE_APP_LEARNING_DASHBOARD_WIDGET_KEY}
            data-key-id={environment.VITE_APP_LEARNING_DASHBOARD_KEY_ID}
            data-widget-id={environment.VITE_APP_LEARNING_DASHBOARD_WIDGET_ID}
          />
        ) : null}
      </Helmet>
    </HelmetProvider>
  );
};
